import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, Stack, IconButton, CircularProgress, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CVHttpService } from '../../services';
import SkillFormDialog from '../dialogs/SkillFormDialog';

function SkillsStep({ cvId }) {
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);

    useEffect(() => {
        if (cvId) {
            loadSkills();
        }
    }, [cvId]);

    const loadSkills = async () => {
        if (!cvId) return;
        setLoading(true);
        try {
            const service = new (await import('core/services/http')).HttpService();
            const data = await service.get(`/job/cv/userskill/?cv=${cvId}`, null);
            // Handle both array response and paginated response
            const skillsList = Array.isArray(data) ? data : (data?.results || []);
            setSkills(skillsList);
        } catch (err) {
            console.error('Failed to load skills', err);
            setSkills([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this skill?')) return;
        try {
            await CVHttpService.deleteUserSkill(id);
            loadSkills();
        } catch (err) {
            alert('Failed to delete skill');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                    Skills
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setEditingSkill(null);
                        setOpenDialog(true);
                    }}
                >
                    Add Skill
                </Button>
            </Stack>

            {!Array.isArray(skills) || skills.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography color="text.secondary">
                        No skills added yet. Click "Add Skill" to get started.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2}>
                    {Array.isArray(skills) && skills.map((skill) => (
                        <Paper key={skill.id} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="start">
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="h6" fontWeight={600}>
                                            {skill.title}
                                        </Typography>
                                        {skill.years_of_experience && (
                                            <Chip 
                                                label={`${skill.years_of_experience} years`} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                    {skill.skill?.title && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Category: {skill.skill.title}
                                        </Typography>
                                    )}
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setEditingSkill(skill);
                                            setOpenDialog(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(skill.id)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            )}

            <SkillFormDialog
                open={openDialog}
                onClose={() => {
                    setOpenDialog(false);
                    setEditingSkill(null);
                }}
                onSave={() => {
                    loadSkills();
                    setOpenDialog(false);
                    setEditingSkill(null);
                }}
                cvId={cvId}
                skill={editingSkill}
            />
        </Box>
    );
}

export default SkillsStep;

